import { Box, Skeleton, Stack, Flex } from '@chakra-ui/react';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import DetailsView from '@/components/Templates/DetailsView';
import { formatDate } from '@/utils';
import type { Shipment } from '@/types/requests';
import { shippingMethodItems } from '@/constants/orderConstants';

interface ShipmentModalContentProps {
	orderData: any;
	isLoading: boolean;
}

/**
 * Shipment delay status mapping
 */
const SHIPMENT_DELAY_MAP: Record<number, string> = {
	953870000: 'Delayed',
	953870001: 'Delivered',
	953870002: 'Additional Product Sent',
	953870003: 'Re-Route',
	953870004: 'Lost',
	953870005: 'Weather Delay',
	953870006: 'Return',
};

/**
 * Get readable shipment delay label
 */
export const getShipmentDelayLabel = (code?: number): string => {
	if (code === undefined || code === null) return '-';
	return SHIPMENT_DELAY_MAP[code] || '-';
};

/**
 * Get readable FedEx shipping method label
 */
const getFedExShippingMethodLabel = (code?: string | number): string => {
	if (!code) return '-';
	const codeStr = String(code);
	const method = shippingMethodItems.find(item => item.value === codeStr);
	return method?.label || codeStr;
};

/**
 * Format boolean to Yes/No
 */
const formatBoolean = (value?: boolean): string => {
	if (value === undefined || value === null) return '-';
	return value ? 'Yes' : 'No';
};

/**
 * Parse shipment data into DetailsView format
 */
const parseShipmentDetails = (shipment: Shipment) => {
	const trackingUrl = shipment.ta_trackingpageurl || 
		(shipment.ta_trackingnumber 
			? `https://www.fedex.com/fedextrack/?trknbr=${shipment.ta_trackingnumber}`
			: undefined);

	return [
		{
			data: [
				{ label: 'Shipment Status', value: shipment.ta_fedexshipmentstatus || '-' },
				{ 
					label: 'Tracking URL', 
					value: shipment.ta_trackingnumber || '-',
					link: trackingUrl,
				},
				{ label: 'Is Primary?', value: formatBoolean(shipment.ta_isprimary) },
				{ label: 'Send Email', value: formatBoolean(shipment.ta_sendemail) },
				{ label: 'Actual Delivery Date', value: formatDate(shipment.ta_actualdeliverydate) },
			],
		},
		{
			data: [
				{ label: 'Estimated Delivery Date', value: formatDate(shipment.ta_estimateddeliverydate) },
				{ label: 'Shipment Update Date', value: formatDate(shipment.ta_shipmentupdatedate) },
				{ label: 'Shipping Method', value: getFedExShippingMethodLabel(shipment.ta_fedexshippingmethod) },
				{ label: 'Tracking Status Response', value: shipment.ta_trackingstatusresponse || '-' },
				{ label: 'Ship Date', value: formatDate(shipment.createdon) },
			],
		},
		{
			data: [
				{ label: 'Delayed', value: getShipmentDelayLabel(shipment.ta_shipmentdelay) },
				{ label: 'Shipment Delay', value: shipment.ta_shipmentdelay ? (shipment.ta_delayed ? 'Yes' : 'No') : '-' },
			],
		},
	];
};

/**
 * Parse inventory (returns) data into DetailsView format - includes product details
 */
const parseInventoryShipmentDetails = (inventory: any) => {
	const trackingUrl = inventory.ta_trackingnumber 
		? `https://www.fedex.com/fedextrack/?trknbr=${inventory.ta_trackingnumber}`
		: undefined;

	return [
		{
			data: [
				{ label: 'Product', value: inventory.product?.ta_name || '-' },
				{ label: 'Q-Code', value: inventory.product?.ta_qcode || '-' },
				{ label: 'Size', value: inventory.product?.ta_size || '-' },
				{ label: 'Manufacturer', value: inventory.product?.ta_manufacturer || '-' },
			],
		},
		{
			data: [
				{ label: 'Shipment Status', value: inventory.ta_fedexshipmentstatus || '-' },
				{ 
					label: 'Tracking Number', 
					value: inventory.ta_trackingnumber || '-',
					link: trackingUrl,
				},
				{ label: 'Shipment Update Date', value: formatDate(inventory.ta_shipmentupdatedate) },
				{ label: 'Shipping Method', value: getFedExShippingMethodLabel(inventory.ta_fedexshippingmethod) },
			],
		},
		{
			data: [
				{ label: 'FedEx Last Updated', value: formatDate(inventory?.ta_fedexshipmentlastupdated) },
				{ label: 'Shipment Delay', value: getShipmentDelayLabel(inventory.ta_shipmentdelay) },
			],
		},
	];
};

const ShipmentModalContent = ({ orderData, isLoading }: ShipmentModalContentProps) => {
	const order = orderData?.data;
	// Support both shipments (for regular orders) and inventories (for returns)
	const allShipments = order?.shipments as Shipment[] | undefined;
	const allInventories = order?.inventories as any[] | undefined;
	const isInventoryMode = !!allInventories && !allShipments;
	const items = (allShipments || allInventories) as any[] | undefined;
	
	const hasItems = !!(items && items.length > 0);
	const hasData = !isLoading && hasItems;
	const noData = !isLoading && (!order || !hasItems);


	return (
		<Box p={4}>
			<Show when={isLoading}>
				<Skeleton height="40px" mb={4} />
				<Skeleton height="200px" mb={4} />
				<Skeleton height="200px" />
			</Show>

			<Show when={noData}>
				<Box textAlign="center" py={8}>
					<FaceliftText fontSize="lg" fontWeight="bold">
						No Shipment Information
					</FaceliftText>
					<FaceliftText fontSize="sm">
						This order has not been shipped yet.
					</FaceliftText>
				</Box>
			</Show>

			<Show when={hasData}>
				<Stack gap={4}>
					{items?.map((item, index) => (
						<Box
							key={item.ta_inventoryid || index}
							p={4}
						>
							<Flex
								mb={4}
								pb={3}
								align="center"
								justify="space-between"
							>
								<FaceliftText fontSize="lg" fontWeight="bold">
									{item.ta_tissueid || `Shipment ${index + 1}`}
							</FaceliftText>
							</Flex>

							{/* Details Grid */}
						<DetailsView
								data={isInventoryMode ? parseInventoryShipmentDetails(item) : parseShipmentDetails(item)}
								gridConfig={{ base: '1fr', md: 'repeat(3, 1fr)' }}
								gap={{ base: 3, md: 6 }}
						/>
						</Box>
					))}
				</Stack>
			</Show>
		</Box>
	);
};

export default ShipmentModalContent;

